import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { track } from "@/track";
import TiltCard from "@/components/landing/TiltCard";
import { useLang } from "@/i18n";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const plans = [
  { name: "Basic", price: 129, launchPrice: 99, days: 60, featured: false, desc: "plan_d_basic" },
  { name: "Standard", price: 139, days: 60, featured: false, desc: "plan_d_standard" },
  { name: "Platinum", price: 159, days: 30, featured: true, desc: "plan_d_platinum" },
  { name: "Custom", custom: true, featured: false, desc: "plan_d_custom" },
];

const sharedFeatures = ["feat_1", "feat_2", "feat_3", "feat_4"];

export default function Plans() {
  const { t } = useLang();

  return (
    <section id="plans" className="py-28 lg:py-40 bg-navy-800/40 border-y border-brand/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-2xl">
          <p className="overline-label" data-testid="plans-overline">{t("plans_over")}</p>
          <h2 className="mt-6 font-serif font-semibold text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="plans-heading">
            {t("plans_h1a")} <span className="text-brand-300">{t("plans_h1b")}</span>
          </h2>
          <p className="mt-6 text-base text-cream/55 leading-relaxed" data-testid="plans-subcopy">
            {t("plans_sub")}
          </p>
          <p className="mt-4 text-xs text-brand-300/90 leading-relaxed border-l-2 border-brand/40 pl-4" data-testid="plans-locations">
            {t("plans_locations")}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {plans.map((plan, i) => (
            <TiltCard
              key={plan.name}
              delay={i * 0.14}
              testid={`plan-card-${plan.name.toLowerCase()}`}
              className={`relative flex flex-col p-10 ${
                plan.featured
                  ? "bg-navy-800 border-2 border-brand shadow-[0_0_60px_rgba(99,102,241,0.30)]"
                  : "bg-navy-800 border border-brand/10 hover:border-brand/30"
              }`}
            >
              {plan.featured && (
                <span
                  className="absolute -top-4 left-10 inline-flex items-center gap-2 bg-brand text-white text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2"
                  data-testid={`plan-badge-${plan.name.toLowerCase()}`}
                >
                  <Star size={12} fill="currentColor" /> {t("most_popular")}
                </span>
              )}

              <h3 className="font-serif font-semibold text-2xl text-cream" data-testid={`plan-name-${plan.name.toLowerCase()}`}>
                {plan.name} Plan
              </h3>

              <div className="mt-6">
                {plan.custom ? (
                  <p className="font-serif text-3xl text-brand leading-tight" data-testid="plan-price-custom">
                    {t("tailored")}
                  </p>
                ) : (
                  <>
                    {plan.launchPrice && (
                      <p className="text-xl text-cream/35 line-through" data-testid="plan-price-basic-original">
                        CAD ${plan.price}
                      </p>
                    )}
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs uppercase tracking-[0.2em] text-brand">CAD</span>
                      <span
                        className="text-6xl font-semibold text-brand leading-none"
                        data-testid={`plan-price-${plan.name.toLowerCase()}`}
                      >
                        ${plan.launchPrice || plan.price}
                      </span>
                      <span className="text-xs text-cream/40">{t("per_applicant")}</span>
                    </div>
                    {plan.launchPrice && (
                      <span
                        className="mt-4 inline-block bg-brand/15 border border-brand/40 text-brand-300 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2"
                        data-testid="plan-basic-launch-badge"
                      >
                        {t("launch_badge")}
                      </span>
                    )}
                  </>
                )}
              </div>

              <p className="mt-6 text-sm text-cream/55 leading-relaxed flex-none">{t(plan.desc)}</p>

              <ul className="mt-8 space-y-4 flex-1">
                {sharedFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-cream/70">
                    <Check size={16} className="mt-0.5 text-brand flex-none" />
                    {t(f)}
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                onClick={() => {
                  track("plan_choose", plan.name);
                  sessionStorage.setItem("ea_prefill", `I'm interested in the ${plan.name} Plan. Please share the next steps.`);
                }}
                data-testid={`plan-select-${plan.name.toLowerCase()}-btn`}
                className={`mt-10 text-center text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 transition-colors duration-300 ${
                  plan.featured
                    ? "bg-brand text-white hover:bg-brand-600"
                    : "border border-brand/40 text-brand hover:bg-brand/10"
                }`}
              >
                {t("choose")} {plan.name}
              </a>
            </TiltCard>
          ))}
        </div>

        <motion.div {...reveal} className="mt-14 max-w-3xl space-y-3" data-testid="plans-disclaimer">
          <p className="text-xs text-brand-300/90 leading-relaxed" data-testid="plans-eligibility">
            {t("plans_eligibility")}
          </p>
          <p className="text-xs text-cream/40 leading-relaxed">
            {t("dis_1")}
          </p>
          <p className="text-xs text-cream/40 leading-relaxed">
            {t("dis_2")}
          </p>
        </motion.div>

        <motion.div
          {...reveal}
          className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border border-brand/20 bg-navy-900 p-10"
          data-testid="plans-help-banner"
        >
          <div>
            <h3 className="font-serif font-semibold text-2xl sm:text-3xl text-cream">{t("help_h")}</h3>
            <p className="mt-2 text-sm text-cream/55">
              {t("help_x")}
            </p>
          </div>
          <a
            href="#contact"
            data-testid="plans-contact-us-btn"
            className="flex-none bg-brand text-white text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-brand-600 transition-colors duration-300"
          >
            {t("help_btn")}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
